import 'react-app-polyfill/ie11'; // < This must be first.
import 'react-app-polyfill/stable';
import 'core-js/es/map';
import 'core-js/es/set';
import 'core-js/es/promise';
import 'core-js/es/object';
import 'core-js/es/string';
import 'core-js/es/string/repeat';
import '@utils/array-extensions';
import '@utils/date-extensions';
import '@utils/string-extensions';
import './styles/index.scss';

import environment from '@common/relay/environment';
import Config from '@config';
import AppRouter from '@containers/AppNavigator/router';
import { featureFlagDefaults } from '@utils/launch-darkly';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { RelayEnvironmentProvider } from 'relay-hooks';

import * as serviceWorker from './serviceWorker';

/*
    LaunchDarkly feature flags.

    `withLDProvider` initialises the client on mount rather than blocking the first paint, so a slow
    or unreachable LaunchDarkly never delays the app. Flags start at the defaults in
    `@utils/launch-darkly` and stream in once the client is ready. If LaunchDarkly cannot be reached
    at all the defaults simply stand, which keeps the offline PWA working.

    The client-side ID comes from `REACT_APP_LAUNCHDARKLY_CLIENT_ID` — `.env.development` locally, a
    Bitbucket deployment variable per environment in CI. It is public by design and safe to ship in the
    bundle; the `sdk-*` server-side key must never appear here. When it is not set the provider is left
    out entirely, so `useFeatureFlag` serves the defaults rather than the SDK erroring on an empty ID.
*/
const withFeatureFlags = (App: React.ComponentType): React.ComponentType => {
    if (!Config.LAUNCHDARKLY_CLIENT_ID) {
        console.warn('LaunchDarkly client-side ID not set; feature flags will use their default values.');
        return App;
    }

    return withLDProvider({
        clientSideID: Config.LAUNCHDARKLY_CLIENT_ID,
        flags: featureFlagDefaults,
        reactOptions: { useCamelCaseFlagKeys: false },
        // Persist the last known flag values so an offline return visit keeps its evaluation
        // instead of dropping back to defaults.
        options: { bootstrap: 'localStorage' },
    })(App);
};

const Root = withFeatureFlags(AppRouter);

const container = document.getElementById('root');
const root = createRoot(container!);

root.render(
    <RelayEnvironmentProvider environment={environment}>
        <Suspense fallback={<div />}>
            <Root />
        </Suspense>
    </RelayEnvironmentProvider>
);

serviceWorker.register();
